/**
 * Fetch an App Bridge v4 session token from the global `shopify` object.
 * Tokens live ~1 minute. Worker `/status/:shop` + `/commands/:shop` verify
 * via SHOPIFY_CLIENT_SECRET (HS256).
 *
 * App Bridge v4 (cdn.shopify.com/shopifycloud/app-bridge.js) exposes the
 * session token via `await shopify.idToken()`. The older
 * `shopify.session.getSessionToken()` shape is from the React-hook
 * package (@shopify/app-bridge-utils) and is NOT present on the global.
 * We accept either shape so the same UI works under either bootstrap.
 *
 * Docs: https://shopify.dev/docs/api/app-bridge-library/reference/session-token
 */

type IdTokenShim = {
  idToken?: () => Promise<string>;
  session?: { getSessionToken: () => Promise<string> };
  config?: { shop: string; host: string; apiKey: string };
};

declare global {
  interface Window {
    shopify?: IdTokenShim;
  }
}

function hasSessionApi(s: IdTokenShim | undefined): boolean {
  return Boolean(s && (typeof s.idToken === "function" || s.session?.getSessionToken));
}

/**
 * Poll for whichever session API App Bridge ends up exposing. The global
 * `window.shopify` appears synchronously when the cdn script loads, but
 * `idToken` / `session.getSessionToken` show up after host-param parsing
 * — typically <100 ms but occasionally >500 ms.
 */
async function waitForAppBridge(timeoutMs = 5000): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (hasSessionApi(window.shopify)) return;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  throw new Error(
    "App Bridge did not expose idToken() or session.getSessionToken within 5 seconds. " +
      "Open this app from your Shopify admin (not directly).",
  );
}

export async function getSessionToken(): Promise<string> {
  await waitForAppBridge();
  const s = window.shopify!;
  if (typeof s.idToken === "function") return s.idToken();
  return s.session!.getSessionToken();
}

export function getShopifyConfig(): { shop: string; host: string; apiKey: string } | null {
  return window.shopify?.config ?? null;
}
